import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  ArrowLeft, Plus, Trash2, Loader2, Sparkles, Home, Trophy,
  Layers, Pencil, Copy, LayoutGrid, Save, X, Image as ImageIcon
} from 'lucide-react';

const ICONS = [
  { id: 'sparkles', Icon: Sparkles },
  { id: 'home', Icon: Home },
  { id: 'trophy', Icon: Trophy },
  { id: 'grid', Icon: LayoutGrid },
];

const COLORS = ['#f59e0b', '#8B5CF6', '#EC4899', '#10b981', '#3b82f6', '#ef4444'];

export default function AdminCarouselTemplatesPage() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/carousel/templates');
      setTemplates(res.data.templates || []);
    } catch {
      toast.error('Failed to load carousel templates');
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  const handleDuplicate = async (tpl) => {
    try {
      await apiClient.post('/carousel/templates', {
        name: `${tpl.name} (Copy)`,
        description: tpl.description,
        category: tpl.category,
        color: tpl.color,
        icon: tpl.icon,
        aspect_ratio: tpl.aspect_ratio || '1:1',
        slides: tpl.slides || [],
      });
      toast.success('Template duplicated');
      loadTemplates();
    } catch {
      toast.error('Failed to duplicate');
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await apiClient.delete(`/carousel/templates/${templateId}`);
      toast.success('Template deleted');
      loadTemplates();
    } catch {
      toast.error('Failed to delete');
    }
  };

  if (editing) {
    return (
      <TemplateEditor
        template={editing === 'new' ? null : editing}
        onBack={() => { setEditing(null); loadTemplates(); }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="admin-carousel-templates-page">
      <div className="border-b border-border/50 px-4 py-2 flex items-center gap-3 bg-card/50">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        <LayoutGrid className="h-4 w-4 text-amber-500" />
        <h1 className="text-base font-semibold">Carousel Template Admin</h1>
      </div>

      <div className="max-w-6xl mx-auto p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-lg font-semibold">Carousel Templates</h2>
            <p className="text-sm text-muted-foreground">{templates.length} templates</p>
          </div>
          <Button size="sm" onClick={() => setEditing('new')} data-testid="new-carousel-template-btn">
            <Plus className="h-4 w-4 mr-1" /> New Template
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : templates.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <LayoutGrid className="h-10 w-10 mx-auto mb-3 opacity-30" />
            <p>No carousel templates yet</p>
            <Button size="sm" className="mt-3" onClick={() => setEditing('new')}>Create your first template</Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.map((tpl) => {
              const IconComp = ICONS.find(i => i.id === tpl.icon)?.Icon || Sparkles;
              const slides = tpl.slides || [];
              return (
                <div key={tpl.template_id} className="border border-border/50 rounded-xl overflow-hidden bg-card hover:shadow-md transition-shadow"
                  data-testid={`carousel-tpl-card-${tpl.template_id}`}>
                  <div className="relative h-28 bg-muted/30">
                    <div className="absolute top-0 left-0 right-0 h-1.5" style={{ backgroundColor: tpl.color }} />
                    <div className="w-full h-full flex items-center justify-center gap-1 px-4">
                      {slides.length > 0 ? slides.slice(0, 5).map((s, i) => (
                        <div key={i} className="flex-1 h-20 rounded-md border border-border/30 flex items-center justify-center text-[9px] text-muted-foreground" style={{ backgroundColor: s.background_color || '#1a1a2e' }}>
                          {s.background_image ? <ImageIcon className="h-4 w-4 opacity-30" /> : `S${i + 1}`}
                        </div>
                      )) : (
                        <div className="flex items-center justify-center" style={{ color: tpl.color }}>
                          <IconComp className="h-10 w-10 opacity-30" />
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="p-3">
                    <div className="flex items-start gap-2 mb-2">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${tpl.color}15` }}>
                        <IconComp className="h-4 w-4" style={{ color: tpl.color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-sm truncate">{tpl.name}</h3>
                        <p className="text-[11px] text-muted-foreground line-clamp-1">{tpl.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 text-[11px] text-muted-foreground mb-3">
                      <span className="flex items-center gap-1"><Layers className="h-3 w-3" /> {slides.length} slides</span>
                      <span className="capitalize">{tpl.category}</span>
                    </div>
                    <div className="flex gap-1.5">
                      <Button variant="outline" size="sm" className="flex-1 text-xs" onClick={() => setEditing(tpl)} data-testid={`edit-carousel-tpl-${tpl.template_id}`}>
                        <Pencil className="h-3 w-3 mr-1" /> Edit
                      </Button>
                      <Button variant="outline" size="sm" className="text-xs" onClick={() => handleDuplicate(tpl)}>
                        <Copy className="h-3 w-3" />
                      </Button>
                      <Button variant="outline" size="sm" className="text-xs text-destructive" onClick={() => handleDelete(tpl.template_id)}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// Inline template editor form
function TemplateEditor({ template, onBack }) {
  const [name, setName] = useState(template?.name || '');
  const [description, setDescription] = useState(template?.description || '');
  const [category, setCategory] = useState(template?.category || 'listing');
  const [color, setColor] = useState(template?.color || '#f59e0b');
  const [icon, setIcon] = useState(template?.icon || 'sparkles');
  const [aspectRatio, setAspectRatio] = useState(template?.aspect_ratio || '1:1');
  const [slideCount, setSlideCount] = useState(template?.slides?.length || 5);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      const slides = Array.from({ length: slideCount }, (_, i) => ({
        background_color: '#1a1a2e',
        background_image: null,
        layers: i === 0 ? [
          { id: `tpl_title_${i}`, type: 'text', x: 100, y: 400, width: 880, height: 80, content: '{{property_type}}', fontSize: 56, fontColor: 'white', fontWeight: 'bold', fontFamily: 'Playfair Display', textAlign: 'center', opacity: 1 },
          { id: `tpl_price_${i}`, type: 'text', x: 100, y: 500, width: 880, height: 60, content: '{{price}}', fontSize: 48, fontColor: color, fontWeight: 'bold', fontFamily: 'Montserrat', textAlign: 'center', opacity: 1 },
        ] : i === slideCount - 1 ? [
          { id: `tpl_cta_${i}`, type: 'text', x: 140, y: 450, width: 800, height: 80, content: 'Schedule a Showing', fontSize: 42, fontColor: 'white', fontWeight: 'bold', fontFamily: 'Montserrat', textAlign: 'center', opacity: 1 },
          { id: `tpl_addr_${i}`, type: 'text', x: 140, y: 550, width: 800, height: 50, content: '{{address}}', fontSize: 28, fontColor: '#aaa', fontWeight: 'normal', fontFamily: 'Montserrat', textAlign: 'center', opacity: 1 },
        ] : [],
      }));

      const payload = { name, description, category, color, icon, aspect_ratio: aspectRatio, slides };

      if (template?.template_id) {
        await apiClient.put(`/carousel/templates/${template.template_id}`, payload);
        toast.success('Template updated');
      } else {
        await apiClient.post('/carousel/templates', payload);
        toast.success('Template created');
      }
      onBack();
    } catch {
      toast.error('Failed to save template');
    }
    setSaving(false);
  };

  return (
    <div className="min-h-screen bg-background" data-testid="carousel-template-editor">
      <div className="border-b border-border/50 px-4 py-2 flex items-center gap-3 bg-card/50">
        <Button variant="ghost" size="sm" onClick={onBack}><X className="h-4 w-4 mr-1" /> Cancel</Button>
        <h1 className="text-base font-semibold">{template ? 'Edit' : 'New'} Carousel Template</h1>
        <div className="flex-1" />
        <Button size="sm" onClick={handleSave} disabled={saving} data-testid="save-carousel-template-btn">
          {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
          Save Template
        </Button>
      </div>

      <div className="max-w-2xl mx-auto p-6 space-y-6">
        <div className="space-y-2">
          <Label>Name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g., Property Showcase" data-testid="tpl-name-input" />
        </div>
        <div className="space-y-2">
          <Label>Description</Label>
          <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Brief description..." data-testid="tpl-desc-input" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Category</Label>
            <select className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={category} onChange={(e) => setCategory(e.target.value)}>
              <option value="listing">Listing</option>
              <option value="open_house">Open House</option>
              <option value="sold">Sold</option>
              <option value="general">General</option>
            </select>
          </div>
          <div className="space-y-2">
            <Label>Aspect Ratio</Label>
            <select className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={aspectRatio} onChange={(e) => setAspectRatio(e.target.value)}>
              <option value="1:1">1:1 (Square)</option>
              <option value="4:5">4:5 (Portrait)</option>
              <option value="9:16">9:16 (Story)</option>
            </select>
          </div>
        </div>
        <div className="space-y-2">
          <Label>Color</Label>
          <div className="flex gap-2">
            {COLORS.map(c => (
              <button key={c} className={`w-8 h-8 rounded-full border-2 ${color === c ? 'border-white scale-110' : 'border-transparent'}`}
                style={{ backgroundColor: c }} onClick={() => setColor(c)} />
            ))}
          </div>
        </div>
        <div className="space-y-2">
          <Label>Icon</Label>
          <div className="flex gap-2">
            {ICONS.map(({ id, Icon }) => (
              <button key={id} className={`w-10 h-10 rounded-lg flex items-center justify-center border ${icon === id ? 'border-primary bg-primary/10' : 'border-border'}`}
                onClick={() => setIcon(id)}>
                <Icon className="h-5 w-5" />
              </button>
            ))}
          </div>
        </div>
        <div className="space-y-2">
          <Label>Number of Slides</Label>
          <Input type="number" value={slideCount} onChange={(e) => setSlideCount(Math.max(1, Math.min(10, parseInt(e.target.value) || 5)))} min={1} max={10} />
        </div>
      </div>
    </div>
  );
}
